// ============================================================
// FILE: src/components/DotGlobe.jsx  →  ROTATING GLOBE
// The animated dotted globe with glowing trade routes,
// used on the homepage hero and the Countries page.
// CITIES list below = the glowing port dots. ARCS = the routes.
// ============================================================

import { useEffect, useRef } from "react";

const CITIES = [
  { name: "Mumbai", lat: 19.07, lng: 72.87 },
  { name: "Dubai", lat: 25.2, lng: 55.27 },
  { name: "Singapore", lat: 1.35, lng: 103.82 },
  { name: "Rotterdam", lat: 51.92, lng: 4.48 },
  { name: "New York", lat: 40.71, lng: -74.0 },
  { name: "Sydney", lat: -33.87, lng: 151.21 },
  { name: "Lagos", lat: 6.52, lng: 3.37 },
  { name: "London", lat: 51.5, lng: -0.12 },
  { name: "Shanghai", lat: 31.23, lng: 121.47 },
  { name: "Santos", lat: -23.96, lng: -46.33 },
  { name: "Tokyo", lat: 35.68, lng: 139.69 },
  { name: "Cape Town", lat: -33.92, lng: 18.42 },
];

const ARCS = [
  [0, 1], [1, 3], [0, 2], [2, 8], [3, 4], [2, 5], [6, 3], [9, 3], [1, 8], [2, 10], [6, 11], [4, 7],
];

function latLngToVec(lat, lng, r) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return {
    x: -r * Math.sin(phi) * Math.cos(theta),
    y: r * Math.cos(phi),
    z: r * Math.sin(phi) * Math.sin(theta),
  };
}

function slerp(a, b, t) {
  const dot = a.x * b.x + a.y * b.y + a.z * b.z;
  const theta = Math.acos(Math.min(1, Math.max(-1, dot)));
  const sinT = Math.sin(theta) || 1e-6;
  const w1 = Math.sin((1 - t) * theta) / sinT;
  const w2 = Math.sin(t * theta) / sinT;
  return { x: a.x * w1 + b.x * w2, y: a.y * w1 + b.y * w2, z: a.z * w1 + b.z * w2 };
}

export default function DotGlobe({ className = "", density = 750, speed = 0.0016 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let W = 0, H = 0, R = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const points = [];
    const N = density;
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const theta = golden * i;
      points.push({ x: Math.cos(theta) * rad, y, z: Math.sin(theta) * rad });
    }

    const cityVecs = CITIES.map((c) => latLngToVec(c.lat, c.lng, 1));
    const arcSeeds = ARCS.map(([a, b], i) => ({ a: cityVecs[a], b: cityVecs[b], offset: i * 0.13 }));

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      W = rect.width; H = rect.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      R = Math.min(W, H) * 0.36;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let rot = 0;
    const draw = () => {
      rot += speed;
      ctx.clearRect(0, 0, W, H);
      const cx = W / 2, cy = H / 2;
      const cosR = Math.cos(rot), sinR = Math.sin(rot);

      // halo
      const grad = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R * 1.35);
      grad.addColorStop(0, "rgba(20,184,166,0.10)");
      grad.addColorStop(0.6, "rgba(30,64,175,0.06)");
      grad.addColorStop(1, "rgba(2,6,23,0)");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.35, 0, Math.PI * 2); ctx.fill();

      // dots
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const x = p.x * cosR - p.z * sinR;
        const z = p.x * sinR + p.z * cosR;
        const y = p.y;
        const px = cx + x * R, py = cy - y * R;
        const depth = (z + 1) / 2;
        const alpha = 0.06 + depth * 0.5;
        const size = 0.6 + depth * 1.1;
        ctx.fillStyle = `rgba(56,189,248,${alpha.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(px, py, size, 0, Math.PI * 2); ctx.fill();
      }

      // arcs
      const t = (performance.now() / 1000) % 1;
      for (const arc of arcSeeds) {
        const segs = 42;
        ctx.beginPath();
        let started = false;
        for (let s = 0; s <= segs; s++) {
          const tt = s / segs;
          const p = slerp(arc.a, arc.b, tt);
          const lift = 1 + Math.sin(Math.PI * tt) * 0.28;
          const x = p.x * Math.cos(rot) - p.z * Math.sin(rot);
          const z = p.x * Math.sin(rot) + p.z * Math.cos(rot);
          const y = p.y;
          const px = cx + x * R * lift, py = cy - y * R * lift;
          if (z > -0.15) {
            if (!started) { ctx.moveTo(px, py); started = true; } else ctx.lineTo(px, py);
          } else started = false;
        }
        ctx.strokeStyle = "rgba(20,184,166,0.28)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // moving pulse
        const tt = (t + arc.offset) % 1;
        const p = slerp(arc.a, arc.b, tt);
        const lift = 1 + Math.sin(Math.PI * tt) * 0.28;
        const x = p.x * cosR - p.z * sinR;
        const z = p.x * sinR + p.z * cosR;
        if (z > -0.15) {
          const px = cx + x * R * lift, py = cy - p.y * R * lift;
          const g = ctx.createRadialGradient(px, py, 0, px, py, 6);
          g.addColorStop(0, "rgba(45,212,191,0.95)");
          g.addColorStop(1, "rgba(45,212,191,0)");
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill();
        }
      }

      // city markers
      for (const v of cityVecs) {
        const x = v.x * cosR - v.z * sinR;
        const z = v.x * sinR + v.z * cosR;
        if (z > 0.05) {
          const px = cx + x * R, py = cy - v.y * R;
          ctx.fillStyle = "rgba(45,212,191,0.9)";
          ctx.beginPath(); ctx.arc(px, py, 2.2, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = "rgba(45,212,191,0.35)";
          ctx.beginPath(); ctx.arc(px, py, 5.5, 0, Math.PI * 2); ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [density, speed]);

  return <canvas ref={canvasRef} className={className} data-testid="dot-globe" aria-label="Rotating globe showing global trade routes" />;
}
